import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Badge } from "@/components/ui/PageHeader";
import {
  getTodayFestivals,
  getUpcomingFestivals,
  getFestivalCalendar,
  getFestivalBySlug,
  type Festival,
} from "@/api/utsav";
import clsx from "clsx";
import { FestivalEvents } from "./FestivalEvents";

type Tab = "today" | "upcoming" | "year";

export default function UtsavLab() {
  const [tab, setTab] = useState<Tab>("today");
  const [slug, setSlug] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Utsav — Festival Calendar"
        subtitle="Indian festival calendar 2026–2027, ~102 entries across 10+ traditions."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Utsav" },
        ]}
      />

      <div className="flex gap-1 border-b border-gray-200">
        {(["today", "upcoming", "year"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab === t
                ? "border-primary-600 text-primary-700"
                : "border-transparent text-gray-500 hover:text-gray-700",
            )}
          >
            {t === "today" ? "Today" : t === "upcoming" ? "Next 30 days" : "Full year"}
          </button>
        ))}
      </div>

      {slug && <FestivalDetail slug={slug} onClose={() => setSlug(null)} />}

      {tab === "today" && <TodayTab onOpen={setSlug} />}
      {tab === "upcoming" && <UpcomingTab onOpen={setSlug} />}
      {tab === "year" && <YearTab onOpen={setSlug} />}
    </div>
  );
}

function TodayTab({ onOpen }: { onOpen: (slug: string) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["utsav-today"],
    queryFn: () => getTodayFestivals(),
  });
  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  const day = data ? (
    <p className="text-xs text-gray-500 mb-2">
      Today is {data.data.today} in {data.data.timeZone}, by this browser&apos;s clock.
    </p>
  ) : null;
  if (!data || data.data.items.length === 0)
    return (
      <>
        {day}
        <p className="text-sm text-gray-500">No festivals today.</p>
      </>
    );
  return (
    <>
      {day}
      <ul className="space-y-3">
        {data.data.items.map((f) => (
          <FestivalRow key={f.id} f={f} onOpen={onOpen} />
        ))}
      </ul>
    </>
  );
}

function UpcomingTab({ onOpen }: { onOpen: (slug: string) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ["utsav-upcoming"],
    queryFn: () => getUpcomingFestivals({ days: 30, limit: 50 }),
  });
  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!data || data.data.items.length === 0)
    return <p className="text-sm text-gray-500">Nothing upcoming.</p>;
  return (
    <ul className="space-y-3">
      {data.data.items.map((f) => (
        <FestivalRow key={f.id} f={f} onOpen={onOpen} />
      ))}
    </ul>
  );
}

function YearTab({ onOpen }: { onOpen: (slug: string) => void }) {
  const year = new Date().getFullYear();
  const { data, isLoading } = useQuery({
    queryKey: ["utsav-year", year],
    queryFn: () => getFestivalCalendar({ year }),
  });
  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!data) return null;
  return (
    <div>
      <p className="text-sm text-gray-500 mb-3">
        {data.data.items.length} festivals in {year}
      </p>
      <ul className="space-y-2">
        {data.data.items.map((f) => (
          <FestivalRow key={f.id} f={f} compact onOpen={onOpen} />
        ))}
      </ul>
    </div>
  );
}

/**
 * A festival date is a civil date sent as midnight UTC. Read in UTC, so a
 * browser west of Greenwich does not show the day before.
 */
const civilDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

function FestivalRow({
  f,
  compact,
  onOpen,
}: {
  f: Festival;
  compact?: boolean;
  onOpen: (slug: string) => void;
}) {
  const localName = f.nameHindi;
  const date = civilDate(f.festivalDate);
  return (
    <li
      onClick={() => onOpen(f.slug)}
      className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-3 cursor-pointer hover:border-primary-300"
    >
      <span className="text-2xl shrink-0">🎉</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">{f.name}</span>
          {localName && (
            <span className="text-sm text-gray-500">{localName}</span>
          )}
          {f.isNational && <Badge variant="purple">national</Badge>}
          {f.isCanary && <Badge variant="yellow">canary</Badge>}
        </div>
        <div className="text-xs text-gray-500 mt-0.5">
          {date}
          {f.tradition && ` · ${f.tradition}`}
          {f.observanceType && ` · ${f.observanceType}`}
        </div>
        {!compact && f.significance && (
          <p className="text-sm text-gray-600 mt-1">{f.significance}</p>
        )}
      </div>
    </li>
  );
}

/** GET /utsav/festival/{slug}: the closest instance, its traditions and every year held. */
function FestivalDetail({ slug, onClose }: { slug: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["utsav-festival", slug],
    queryFn: () => getFestivalBySlug(slug),
  });
  return (
    <div className="bg-white border border-primary-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900">
          {data ? data.data.festival.name : slug}
        </span>
        <button onClick={onClose} className="text-xs text-gray-500 hover:text-gray-700">
          Close
        </button>
      </div>
      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">{(error as Error).message}</p>}
      {data && (
        <>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Years held</p>
            <ul className="text-sm text-gray-700 space-y-0.5">
              {data.data.instances.map((i) => (
                <li key={i.id}>
                  {i.year}: {civilDate(i.festivalDate)}
                  {i.festivalEndDate && ` to ${civilDate(i.festivalEndDate)}`}
                </li>
              ))}
            </ul>
          </div>
          <FestivalEvents slug={slug} />
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">
              Traditions ({data.data.festival.traditions.length})
            </p>
            <ul className="space-y-2">
              {data.data.festival.traditions.map((t) => (
                <li key={t.id} className="text-sm text-gray-700">
                  <span className="font-medium">{t.traditionName}</span>
                  {t.states.length > 0 && ` · ${t.states.join(", ")}`}
                  <p className="text-gray-600">{t.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
